import { useFlag, useNewCode } from '@calendly/feature-check-react';
import { SearchForm } from '@calendly/form';
import { useWindowSize } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetEventTypesCollectionQuery } from 'common';
import { LoadingOrEmptyState } from 'components/event_type_modals/loading_empty_state.js';
import { idSelector as currentUserIdSelector } from 'selectors/current_user';
import { EventTypeItem } from './event_type_item.js';
const containerIsPhoneCss = "ckmoqf3";
const containerCss = "c1mi1i29";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const searchCss = "skgcosh";
const Search = forwardRef((props, ref) => <SearchForm ref={ref} {...props} className={classNames(searchCss, props.className)} />);
const sectionCss = "s11q3t36";
const Section = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'section';
  return <Component ref={ref} {...restProps} className={classNames(sectionCss, props.className)} />;
});
const itemsContainerCss = "i1jrolg3";
const ItemsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(itemsContainerCss, props.className)} />;
});
const emptyContainerCss = "em61z7x";
const EmptyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyContainerCss, props.className)} />;
});
const EventTypesGroup = ({
  group,
  onSelect,
  selectedET
}) => {
  const {
    profile
  } = group;
  const currentUserId = useSelector(currentUserIdSelector);
  const isSingleUseLinkEmptyStateEnabled = useNewCode('qol_sul_empty_state', false)?.enabled;
  const isGroupOwner = profile?.owner_type === 'User' && profile?.owner_id === currentUserId;
  const name = isGroupOwner ? I18n.t('publisher.event_types.filters.me') : group.name;
  if (group.event_types.length === 0) return null;
  return <Section>
      <BodyText key={group.name} size="medium" weight="bold">
        {!isSingleUseLinkEmptyStateEnabled ? name : ''}
      </BodyText>
      {group.event_types.map(et => <EventTypeItem key={et.uuid} item={et} isMultipleSelect={false} onClick={() => onSelect(et)} isSelected={et.uuid === selectedET?.uuid} />)}
    </Section>;
};
export const EventTypesList = forwardRef(({
  onSelect,
  selectedET,
  actionKind,
  showStroke,
  hideStroke,
  onCreateEventTypeClick,
  isOpen
}, containerRef) => {
  const [searchValue, setSearchValue] = useState('');
  const {
    width,
    height
  } = useWindowSize();
  const {
    data,
    isLoading
  } = useGetEventTypesCollectionQuery({
    filterTerm: searchValue,
    scope: 'my_calendly',
    skipPagination: true
  }, {
    skip: !isOpen
  });
  const results = data?.results;
  const enabledEventTypes = useMemo(() => results?.map(block => ({
    ...block,
    event_types: block.event_types.filter(({
      active
    }) => active)
  })) ?? [], [results]);
  const hasEventTypes = enabledEventTypes.some(currentValue => currentValue.event_types.length > 0);
  const isContactsShareCTAFeatureEnabled = useFlag('org_expansion_share_cta_feature');
  const isSingleUseLinkEmptyStateEnabled = useNewCode('qol_sul_empty_state', false)?.enabled;
  const headingText = useMemo(() => {
    if (isContactsShareCTAFeatureEnabled && actionKind === 'book_meeting') {
      return I18n.t('publisher.event_types.share.title_select_event_type_to_book');
    }
    if (isSingleUseLinkEmptyStateEnabled && actionKind === 'single_use_link_creation') {
      return I18n.t('publisher.event_types.share.start_from_existing_event_type');
    }
    return I18n.t('publisher.event_types.share.select_event_type');
  }, [isContactsShareCTAFeatureEnabled, actionKind, isSingleUseLinkEmptyStateEnabled]);
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const {
      scrollHeight,
      clientHeight
    } = containerRef.current;
    if (scrollHeight > clientHeight) {
      showStroke();
    } else {
      hideStroke();
    }
  }, [containerRef, width, height, data, hideStroke, showStroke]);
  return <Container>
      <BodyText size="large" weight="bold">
        {headingText}
      </BodyText>
      {hasEventTypes && <Search placeholder={I18n.t('publisher.event_types.filters.search.placeholder.single_scope')} onSearch={setSearchValue} isFullSized />}
      <ItemsContainer>
        {hasEventTypes ? enabledEventTypes.map(group => <EventTypesGroup key={group.name} group={group} onSelect={onSelect} selectedET={selectedET} />) : <EmptyContainer>
            <LoadingOrEmptyState hasLoadedSearchItems={hasEventTypes} hasSearchTermOrAppliedFilters={!!searchValue} isLoading={isLoading} onCreateEventTypeClick={onCreateEventTypeClick} />
          </EmptyContainer>}
      </ItemsContainer>
    </Container>;
});

require("./event_types_list.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./event_types_list.js");